import Vue from 'vue'
import * as LDClient from 'launchdarkly-js-client-sdk'
import { getLaunchDarklySdkKey } from '@/helpers/env'

export const MUTATION_UPDATE_FEATURE_FLAG = 'MUTATION_UPDATE_FEATURE_FLAG'
export const MUTATION_START_SYNCING = 'MUTATION_START_SYNCING'

const LAUNCH_DARKLY_HOST_ENABLE_SHARED_EXPERIENCES =
  'host-app-enable-shared-experiences'

const LAUNCH_DARKLY_ROOS_ENABLE_645_AREA_CODE = 'roos-enable-645-area-code'

const LAUNCH_DARKLY_ROOS_ENABLE_SMS_OPT_IN_A2P_COMPLIANT =
  'OLO-131819-enable-sms-opt-in-a2p-compliant'

const LAUNCH_DARKLY_ROOS_ENABLE_ARKOSE = 'olo-133762-enable-arkose-roos'

const FEATURE_FLAGS = [
  {
    key: LAUNCH_DARKLY_HOST_ENABLE_SHARED_EXPERIENCES,
    defaultValue: false,
  },
  {
    key: LAUNCH_DARKLY_ROOS_ENABLE_645_AREA_CODE,
    defaultValue: false,
  },
  {
    key: LAUNCH_DARKLY_ROOS_ENABLE_SMS_OPT_IN_A2P_COMPLIANT,
    defaultValue: false,
  },
  {
    key: LAUNCH_DARKLY_ROOS_ENABLE_ARKOSE,
    defaultValue: false,
  },
]

let ldclient
let identifyUserPromise: Promise<void> | undefined
const state = {
  flags: {},
  startedSyncing: false,
}

export default {
  namespaced: true,
  state,
  mutations: {
    [MUTATION_UPDATE_FEATURE_FLAG]: (state, { key, value }) => {
      Vue.set(state.flags, key, value)
    },
    [MUTATION_START_SYNCING]: state => {
      state.startedSyncing = true
    },
  },
  actions: {
    startSyncingFeatureFlags: ({ dispatch }) => {
      return Promise.all(
        FEATURE_FLAGS.map(flag => dispatch('syncFeatureFlag', flag))
      )
    },
    syncFeatureFlag: async (
      { dispatch, commit },
      { key, defaultValue = false }
    ) => {
      if (!ldclient) {
        await dispatch('identifyUser')
      }

      commit(MUTATION_UPDATE_FEATURE_FLAG, {
        key,
        value: ldclient.variation(key, defaultValue),
      })

      ldclient.on(`change:${key}`, function (value, previous) {
        commit(MUTATION_UPDATE_FEATURE_FLAG, {
          key,
          value,
        })
      })
    },
    identifyUser: ({ state, commit, dispatch }, { merchantId, programId }) => {
      identifyUserPromise = new Promise<void>(resolve => {
        const startSyncingAndResolve = async () => {
          if (!state.startedSyncing) {
            commit(MUTATION_START_SYNCING)
            await dispatch('startSyncingFeatureFlags')
          }
          resolve()
        }
        const user = {
          key: programId,
          custom: {
            merchantId: Number(merchantId),
          },
        }
        if (!ldclient) {
          ldclient = LDClient.initialize(getLaunchDarklySdkKey(), user)
          ldclient.on('ready', startSyncingAndResolve)
        } else {
          ldclient.identify(user, null, startSyncingAndResolve)
        }
      })

      return identifyUserPromise
    },
    // Lets any action wait for flags to be synced, even if it races
    // getMerchant's identifyUser call (e.g. an auto-search firing before
    // the merchant/program id needed to identify the LD user is known).
    waitForFeatureFlags: async (
      { dispatch },
      { merchantId, programId }: { merchantId: string; programId: string }
    ) => {
      if (!identifyUserPromise) {
        await dispatch('identifyUser', { merchantId, programId })
      } else {
        await identifyUserPromise
      }
    },
  },
  getters: {
    featureFlagEnabled: state => key => {
      return state.flags[key]
    },
    sharedExperiencesEnabled: (state, getters) => {
      return getters.featureFlagEnabled(
        LAUNCH_DARKLY_HOST_ENABLE_SHARED_EXPERIENCES
      )
    },
    areaCode645Enabled: (state, getters) => {
      return getters.featureFlagEnabled(LAUNCH_DARKLY_ROOS_ENABLE_645_AREA_CODE)
    },
    a2pCompliantEnabled: (state, getters) => {
      return getters.featureFlagEnabled(
        LAUNCH_DARKLY_ROOS_ENABLE_SMS_OPT_IN_A2P_COMPLIANT
      )
    },
    arkoseEnabled: (state, getters) => {
      return getters.featureFlagEnabled(LAUNCH_DARKLY_ROOS_ENABLE_ARKOSE)
    },
  },
}
